export type ConsentCategory = {
  necessary: true
  analytics: boolean
  marketing: boolean
}

export type ConsentRecord = {
  anonymousId: string
  categories: ConsentCategory
  policyVersion: string
  acceptedAt: string
  revokedAt?: string
}

export const CONSENT_POLICY_VERSION = '2026-05-lgpd-1'
export const CONSENT_STORAGE_KEY = 'pinday_consent_record'
export const CONSENT_UPDATED_EVENT = 'pinday:consent-updated'
export const CONSENT_APPLIED_EVENT = 'pinday:consent-applied'

const ANONYMOUS_ID_STORAGE_KEY = 'pinday_anonymous_id'

const defaultCategories: ConsentCategory = {
  necessary: true,
  analytics: false,
  marketing: false,
}

function createAnonymousId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `anon_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
}

export function getAnonymousId() {
  if (typeof window === 'undefined') return 'server'

  const existing = window.localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY)
  if (existing) return existing

  const anonymousId = createAnonymousId()
  window.localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, anonymousId)
  return anonymousId
}

export function getConsentRecord(): ConsentRecord | null {
  if (typeof window === 'undefined') return null

  const rawRecord = window.localStorage.getItem(CONSENT_STORAGE_KEY)
  if (!rawRecord) return null

  try {
    const parsed = JSON.parse(rawRecord) as ConsentRecord
    if (parsed.policyVersion !== CONSENT_POLICY_VERSION || parsed.revokedAt) {
      return null
    }

    return {
      anonymousId: parsed.anonymousId || getAnonymousId(),
      policyVersion: parsed.policyVersion,
      acceptedAt: parsed.acceptedAt,
      revokedAt: parsed.revokedAt,
      categories: {
        ...defaultCategories,
        ...parsed.categories,
        necessary: true,
      },
    }
  } catch {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY)
    return null
  }
}

export function hasConsent(category: keyof Omit<ConsentCategory, 'necessary'>) {
  return getConsentRecord()?.categories[category] === true
}

export function saveConsent(categories: Omit<ConsentCategory, 'necessary'>): ConsentRecord {
  const record: ConsentRecord = {
    anonymousId: getAnonymousId(),
    categories: {
      necessary: true,
      analytics: categories.analytics,
      marketing: categories.marketing,
    },
    policyVersion: CONSENT_POLICY_VERSION,
    acceptedAt: new Date().toISOString(),
  }

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: record }))

  return record
}

export function revokeConsent() {
  const previous = getConsentRecord()
  const revokedRecord: ConsentRecord = {
    anonymousId: previous?.anonymousId || getAnonymousId(),
    categories: defaultCategories,
    policyVersion: CONSENT_POLICY_VERSION,
    acceptedAt: previous?.acceptedAt || new Date().toISOString(),
    revokedAt: new Date().toISOString(),
  }

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(revokedRecord))
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: revokedRecord }))

  return revokedRecord
}
